'use client';
import { useI18n } from '@/components/public/i18n/LocaleProvider';

import Link from '@/components/public/i18n/LocalizedLink';
import { stripLocale } from '@/lib/i18n/core';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { ELINA_SESSION_KEY, elinaEntryHref, elinaEntryIdForPath, hasElinaConversation, type ElinaEntryId } from '@/lib/public/elina-entry';
import './elina-access.css';

type Props = { variant?: 'floating' | 'home' | 'inline'; contextId?: ElinaEntryId; hidden?: boolean };

export default function ElinaAccess({ variant = 'floating', contextId, hidden = false }: Props) {
  const { t, href, content, locale } = useI18n();


  const pathname = usePathname() || '/';
  const entryId = contextId || elinaEntryIdForPath(stripLocale(pathname));
  const [resuming, setResuming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [configuring, setConfiguring] = useState(false);

  useEffect(() => {
    const refresh = () => {
      try { setResuming(hasElinaConversation(sessionStorage.getItem(ELINA_SESSION_KEY + (locale === 'fr' ? '' : ':' + locale)))); }
      catch { setResuming(false); }
    };
    refresh();
    window.addEventListener('pageshow', refresh);
    window.addEventListener('focus', refresh);
    return () => { window.removeEventListener('pageshow', refresh); window.removeEventListener('focus', refresh); };
  }, [pathname, locale]);

  useEffect(() => {
    if (variant !== 'floating') return;
    let frame = 0;
    const refreshFocus = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setEditing(!!document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')));
    };
    refreshFocus();
    document.addEventListener('focusin', refreshFocus);
    document.addEventListener('focusout', refreshFocus);
    const form = document.getElementById('configurateur');
    setConfiguring(false);
    const observer = form && typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(entries => setConfiguring(entries.some(entry => entry.isIntersecting))) : null;
    if (form) observer?.observe(form);
    return () => { cancelAnimationFrame(frame); observer?.disconnect(); document.removeEventListener('focusin', refreshFocus); document.removeEventListener('focusout', refreshFocus); };
  }, [pathname, variant]);

  // This component is deliberately absent from the conversation and private workspaces.
  if (!entryId || hidden || (variant === 'floating' && (entryId === 'accueil' || editing || configuring))) return null;
  const portrait = <img src="/assets/universes/elina-chat-cutout.webp" alt="" width={48} height={60} />;
  const arrow = <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12h15m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  const label = resuming ? t('Reprendre avec Élina') : variant === 'home' ? t('Étudier mon projet avec Élina') : variant === 'inline' ? t('Étudier ce besoin avec Élina') : t('Parler à Élina');

  if (variant === 'home') return <div className="rdv-elina-invitation">
    {portrait}<div><strong>{t("Votre ambition, un projet à construire.")}</strong><p>{t("Décrivez votre besoin. Élina vous aide à préparer votre solution digitale sur mesure.")}</p></div>
    <Link href={href(elinaEntryHref(entryId))} prefetch={false} className="rdv-elina-home-link">{label}{arrow}</Link>
  </div>;

  return <Link href={href(elinaEntryHref(entryId))} prefetch={false} className={`rdv-elina-access rdv-elina-access-${variant}`}>
    {portrait}<span>{label}</span>{arrow}
  </Link>;
}


